import { ItemAttribute, Maybe } from '@types';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import Swatch, { SwatchSizes } from './Swatch';

type Props = {
  label: string;
  attributes: ItemAttribute[];
  activeAttribute: Maybe<ItemAttribute>;
  wrapperClassName?: string;
  listClassName?: string;
  itemHandler: (swatch) => () => void;
  indicateSale: any;
  showActiveAttribute?: boolean;
  hideLabel?: boolean;
  swatchSize?: SwatchSizes;
  hideActiveAvailability?: boolean;
  labelSize?: 'md' | 'sm';
  extra?: any;
  hideOOS?: boolean;
};

const Swatches = ({
  label,
  attributes,
  activeAttribute,
  wrapperClassName,
  listClassName,
  itemHandler,
  indicateSale,
  showActiveAttribute = true,
  hideActiveAvailability = false,
  hideLabel = false,
  swatchSize = 'md',
  labelSize = 'md',
  extra,
  hideOOS = false,
}: Props) => {
  const activeAttributeValue = activeAttribute
    ? showActiveAttribute
      ? `${activeAttribute.value}`
      : null
    : `Select ${label.toLowerCase()}`;

  const activeStockAvailability = activeAttribute ? (
    !activeAttribute.isInStock ? (
      <span> - Out Of Stock</span>
    ) : !activeAttribute.isAvailable ? (
      <span> - Unavailable</span>
    ) : null
  ) : null;

  const activeExtra = extra?.[activeAttribute?.key]?.[activeAttribute?.value];

  const shownAttributes = useMemo(
    () =>
      hideOOS ? attributes.filter((a) => a.isInStock || activeAttribute?.value === a.value) : attributes,
    [attributes, hideOOS, activeAttribute]
  );

  return (
    <div
      className={classNames({
        [wrapperClassName]: !!wrapperClassName,
        [`product-${label}s`]: !wrapperClassName,
      })}>
      <div
        className={classNames({
          'tw-mb-2': swatchSize === 'md',
          'tw-mb-1': swatchSize === 'sm',
        })}>
        <div className='tw-flex tw-items-center tw-gap-2'>
          {!hideLabel && (
            <span
              className={classNames('tw-font-normal tw-text-[#555555]', {
                'tw-text-base': labelSize === 'md',
                'tw-text-[13px]': labelSize === 'sm',
              })}>
              {label}
            </span>
          )}{' '}
          <i
            className={classNames('tw-font-italic tw-font-bold tw-text-body', {
              'tw-text-base': labelSize === 'md',
              'tw-text-sm': labelSize === 'sm',
            })}>
            {activeAttributeValue}
            {!hideActiveAvailability && activeStockAvailability}
          </i>
        </div>
      </div>
      <div
        className={classNames('tw-flex tw-flex-wrap tw-gap-2', {
          [listClassName]: !!listClassName,
          [label]: !listClassName,
        })}>
        {shownAttributes.map((swatch) => (
          <Swatch
            key={swatch.id}
            swatch={swatch}
            indicateSale={indicateSale}
            active={activeAttribute?.id === swatch.id}
            itemHandler={itemHandler}
            size={swatchSize}
            extra={extra?.[swatch.key]?.[swatch.value]}
          />
        ))}
      </div>
      {!!activeExtra?.tagline && (
        <div className='tw-my-2 tw-text-gray-dark'>
          <em>{activeExtra.tagline}</em>
        </div>
      )}
    </div>
  );
};

export default memo(Swatches);
